import type {
    IRawData,
    IRecord,
    ITableData,
    ITableRecord,
    ITableRecordPrevious,
    PeriodOverPeriodTableRecordT,
} from "app/pages/industry-analysis/players/table/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getSegmentOrDomainCellEnrichedAttributes } from "app/components/React/Table/cells/SegmentOrDomainCell";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { recordsSorterHof } from "app/UtilitiesAndConstants/UtilityFunctions/sort";
import { ENTITIES_PER_PAGE } from "app/pages/industry-analysis/players/const";
import type { PlayersTableFilters } from "app/pages/industry-analysis/players/table/TableTop";

const domainFilter = (search: string) => (record: ITableRecord) => {
    const { domain, SegmentName } = record;
    return (
        domain.toLowerCase().includes(search.toLowerCase()) ||
        SegmentName?.toLowerCase()?.includes(search.toLowerCase())
    );
};

const getUrlAndSegment = (dataItem: IRecord, params: Record<string, string>, getUserAndAccountSegmentById) => {
    const { Domain: domain, Id: id } = dataItem;
    const url = proNavigator.href("digitalsuite_website_websiteperformance", {
        key: domain,
        isWWW: "*",
        ...params,
    });
    const segment = getUserAndAccountSegmentById(id);
    return { url, segment };
};

type RecordType<T extends boolean> = T extends true ? ITableRecordPrevious : ITableRecord;
const mapDataItemToRecord = <T extends boolean>(
    dataItem: IRecord,
    params: Record<string, string>,
    getUserAndAccountSegmentById: (id: string) => any,
    isCompare: T,
): RecordType<T> => {
    if (isCompare) {
        const {
            AdjacentPeriodChange: previousAdjacentPeriodChange,
            AvgMonthVisits: previousTotalVisits,
            Domain: domain,
            BrandedVolume: previousBrandedVolume,
            BrandedVolumeShare: previousBrandedVolumeShare,
            DirectVisits: previousDirectVisits,
            AvgUniqueUsers: previousAvgUniqueUsers,
            PeriodChange: previousPeriodChange,
            PpcSpend: previousPpcSpend,
            Share: previousShare,
            SiteFunctionality: siteFunctionality,
            Favicon,
            ExclusiveVisitors: previousExclusiveVisitors,
            NewVsReturning: previousNewVsReturning,
            ConversionRate: previousConversionRate,
            ConvertedVisits: previousConvertedVisits,
        } = dataItem;

        const { url, segment } = getUrlAndSegment(dataItem, params, getUserAndAccountSegmentById);

        const record: ITableRecordPrevious = {
            ...getSegmentOrDomainCellEnrichedAttributes(dataItem, segment),
            previousAdjacentPeriodChange,
            previousTotalVisits,
            domain,
            url,
            previousBrandedVolume,
            previousBrandedVolumeShare,
            previousDirectVisits,
            previousAvgUniqueUsers,
            previousPeriodChange,
            previousPpcSpend,
            previousShare,
            siteFunctionality,
            Favicon,
            previousExclusiveVisitors,
            previousNewVsReturning,
            previousConversionRate,
            previousConvertedVisits,
        };

        return record as RecordType<T>;
    } else {
        const {
            AdjacentPeriodChange: adjacentPeriodChange,
            AvgMonthVisits: totalVisits,
            Domain: domain,
            BrandedVolume: brandedVolume,
            BrandedVolumeShare: brandedVolumeShare,
            DirectVisits: directVisits,
            AvgUniqueUsers: avgUniqueUsers,
            PeriodChange: periodChange,
            PpcSpend: ppcSpend,
            Share: share,
            SiteFunctionality: siteFunctionality,
            Favicon,
            ExclusiveVisitors: exclusiveVisitors,
            NewVsReturning: newVsReturning,
            ConversionRate: conversionRate,
            ConvertedVisits: convertedVisits,
        } = dataItem;

        const { url, segment } = getUrlAndSegment(dataItem, params, getUserAndAccountSegmentById);

        const record: ITableRecord = {
            ...getSegmentOrDomainCellEnrichedAttributes(dataItem, segment),
            share,
            Favicon,
            avgUniqueUsers,
            directVisits,
            periodChange,
            ppcSpend,
            brandedVolume,
            brandedVolumeShare,
            domain,
            adjacentPeriodChange,
            url,
            totalVisits,
            exclusiveVisitors,
            newVsReturning,
            siteFunctionality,
            conversionRate,
            convertedVisits,
        };

        return record as RecordType<T>;
    }
};

export const parseRawData = (rawData: IRawData, segmentsModule, tableFilters: PlayersTableFilters): ITableData => {
    const { Data: data = [], CompareData: compareData = [] } = rawData;
    const { search, sort, page, websiteType } = tableFilters;
    const params = proNavigator.getParams();
    const getUserAndAccountSegmentById = SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule);

    const currentDataRecords: Array<ITableRecord> = data.map((dataItem) =>
        mapDataItemToRecord(dataItem, params, getUserAndAccountSegmentById, false),
    );
    const compareDataRecordsById = new Map(
        compareData.map((dataItem) => [
            dataItem.Id,
            mapDataItemToRecord(dataItem, params, getUserAndAccountSegmentById, true),
        ]),
    );
    let filteredRecords: Array<ITableRecord | PeriodOverPeriodTableRecordT> = currentDataRecords;

    if (compareData.length) {
        filteredRecords = currentDataRecords.map((currentRecord, index) => ({
            ...currentRecord,
            ...compareDataRecordsById.get(data[index].Id),
        }));
    }
    if (search) {
        filteredRecords = filteredRecords.filter(domainFilter(search));
    }
    if (websiteType) {
        filteredRecords = filteredRecords.filter(
            ({ siteFunctionality }) => String(siteFunctionality) === websiteType.id,
        );
    }

    const sortedRecords = filteredRecords.sort(recordsSorterHof(sort));
    const pageRecords = sortedRecords.slice(ENTITIES_PER_PAGE * (page - 1), ENTITIES_PER_PAGE * page);
    const tableData: ITableData = { TotalCount: filteredRecords.length, Records: pageRecords };
    return tableData;
};
